import { api } from "./api";
import type {
  BunkerBargeByImoArgs,
  BunkerBargeCandidatesResponse,
  ClaimUnclaimResumeCallParams,
  ClaimUnclaimVesselCallParams,
  CloseVesselCallByIdArgs,
  CompleteResumptionVesselCallByIdArgs,
  CompleteVesselCallByIdArgs,
  DraftVesselCallLookupResponse,
  GetVesselCallsResponse,
  ImportCrewListResponse,
  ImportPassengerListResponse,
  Interruption,
  InterruptVesselCallByIdArgs,
  ResumeInterruptionVesselCallByIdArgs,
  UpdateVesselCallPayload,
  VesselCallCreateRequest,
  VesselCallParams,
  VesselCallResponse,
  VesselRegisterDetailsFixed,
} from "./vesselCall/types";

const SERVICE = "bff-vessel-call";

export const getVesselCallById = async (service: string, id: string) => {
  return api.get<VesselCallResponse>(service, `vessel-calls/${id}`);
};

export const getVesselCallInterruption = async (service: string, vesselCallId: string) => {
  return api.get<Interruption>(service, `vessel-calls/${vesselCallId}/interruptions`);
};

export const getVesselCallDetails = async (service: string, vesselCallId: string) => {
  return api.get<VesselRegisterDetailsFixed>(service, `vessel-calls/${vesselCallId}/vessel-header`);
};

export const getSearchVesselCalls = async (
  service: string,
  payload: {
    pageSize: number;
    pageNumber: number;
    filters: { field: string; values: string[] }[];
  },
) => {
  return api.post<GetVesselCallsResponse>(service, "vessel-calls:search", payload);
};

export const getVesselCallStatusByVcn = async (service: string, vcn: string) => {
  return api.get<{
    isApproved: boolean;
    vesselName?: string;
  }>(service, `vessel-calls/status/${encodeURIComponent(vcn)}`);
};

export const createVesselCall = async (
  payload: VesselCallCreateRequest & {
    id?: string;
  },
  transactionId?: string,
) => {
  return api.post<VesselCallResponse>(
    SERVICE,
    "vessel-calls",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const createDraftVesselCall = async (
  id: string,
  payload: VesselCallCreateRequest,
  transactionId?: string,
) => {
  return api.post<VesselCallResponse>(
    SERVICE,
    `vessel-calls/${id}:save-arrival-drafts`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const createNewDraftVesselCall = async (
  payload: VesselCallCreateRequest,
  transactionId?: string,
) => {
  return api.post<VesselCallResponse>(
    SERVICE,
    "vessel-calls:save-pre-arrival-draft",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const resubmitVesselCall = async (
  id: string,
  payload: VesselCallCreateRequest,
  transactionId?: string,
) => {
  return api.post<VesselCallResponse>(
    SERVICE,
    `vessel-calls/${id}:resubmit`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const updateDraftVesselCall = async (
  id: string,
  payload: VesselCallCreateRequest,
  transactionId?: string,
) => {
  return api.put<VesselCallResponse>(
    SERVICE,
    `vessel-calls/${id}:update-draft`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const deleteDraftVesselCall = async (id: string, transactionId?: string) => {
  return api.delete(
    SERVICE,
    `vessel-calls/${id}:discard-draft`,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const checkDraftVesselCall = async (id: string) => {
  return api.get<DraftVesselCallLookupResponse>(
    SERVICE,
    `vessel-calls/pre-arrival-draft?vesselId=${id}`,
  );
};

export const claimVesselCall = async ({
  service,
  vesselCallId,
  transactionId,
}: ClaimUnclaimVesselCallParams & { transactionId?: string }) => {
  return api.put(
    service,
    `vessel-calls/${vesselCallId}/claim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const unclaimVesselCall = async ({
  service,
  vesselCallId,
  transactionId,
}: ClaimUnclaimVesselCallParams & { transactionId?: string }) => {
  return api.put(
    service,
    `vessel-calls/${vesselCallId}/unclaim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const claimResumeCall = async ({
  service,
  vesselCallId,
  transactionId,
}: ClaimUnclaimResumeCallParams & { transactionId?: string }) => {
  return api.put(
    service,
    `vessel-calls/${vesselCallId}/interruptions:claim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const unclaimResumeCall = async ({
  service,
  vesselCallId,
  transactionId,
}: ClaimUnclaimResumeCallParams & { transactionId?: string }) => {
  return api.put(
    service,
    `vessel-calls/${vesselCallId}/interruptions:unclaim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const completeVesselCallById = async ({
  service,
  vesselCallId,
  params,
  transactionId,
}: {
  service: string;
  vesselCallId: string;
  params: CompleteVesselCallByIdArgs;
  transactionId?: string;
}) => {
  return api.put<VesselCallParams>(
    service,
    `vessel-calls/${vesselCallId}:complete`,
    { ...params },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const closeVesselCallById = async ({
  service,
  vesselCallId,
  payload,
  transactionId,
}: {
  service: string;
  vesselCallId: string;
  payload: CloseVesselCallByIdArgs;
  transactionId?: string;
}) => {
  return api.patch<VesselCallResponse>(
    service,
    `vessel-calls/${vesselCallId}:close`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const updateVesselCallById = async ({
  service,
  vesselCallId,
  payload,
  transactionId,
}: {
  service: string;
  vesselCallId: string;
  payload: UpdateVesselCallPayload;
  transactionId?: string;
}) => {
  return api.patch<VesselCallResponse>(
    service,
    `vessel-calls/${vesselCallId}:update-arrival`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const interruptVesselCallById = async ({
  service,
  vesselCallId,
  params,
  transactionId,
}: {
  service: string;
  vesselCallId: string;
  params: InterruptVesselCallByIdArgs;
  transactionId?: string;
}) => {
  return api.post<VesselCallParams>(
    service,
    `vessel-calls/${vesselCallId}/interruptions:start`,
    { ...params },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const resumeInterruptionVesselCallById = async ({
  service,
  vesselCallId,
  params,
  transactionId,
}: {
  service: string;
  vesselCallId: string;
  params: ResumeInterruptionVesselCallByIdArgs;
  transactionId?: string;
}) => {
  return api.post<VesselCallParams>(
    service,
    `vessel-calls/${vesselCallId}/interruptions:resume`,
    { ...params },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const completeResumptionVesselCallById = async ({
  service,
  vesselCallId,
  params,
  transactionId,
}: {
  service: string;
  vesselCallId: string;
  params: CompleteResumptionVesselCallByIdArgs;
  transactionId?: string;
}) => {
  return api.put<VesselCallParams>(
    service,
    `vessel-calls/${vesselCallId}/interruptions:complete`,
    { ...params },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const getBunkerBargeCandidatesByIMO = async ({
  service,
  params,
}: {
  service: string;
  params: BunkerBargeByImoArgs;
}) => {
  return api.get<BunkerBargeCandidatesResponse>(service, "vessel-calls/bunker-barge-candidates", {
    params,
  });
};

export const getVesselCallExcelTemplate = async ({
  templateType,
}: {
  templateType: string;
}): Promise<Blob> => {
  return api.get(SERVICE, "vessel-calls/excel-template", {
    params: { templateType },
    responseType: "blob",
  });
};

export const importPassengerList = async (
  file: File,
  signal?: AbortSignal,
  transactionId?: string,
) => {
  const formData = new FormData();
  formData.append("file", file);

  return api.post<ImportPassengerListResponse>(
    SERVICE,
    "vessel-calls/passenger-list-template/import",
    formData,
    { signal, ...(transactionId ? { transactionId } : {}) },
  );
};

export const importCrewList = async (file: File, signal?: AbortSignal, transactionId?: string) => {
  const formData = new FormData();
  formData.append("file", file);

  return api.post<ImportCrewListResponse>(
    SERVICE,
    "vessel-calls/crew-list-template/import",
    formData,
    { signal, ...(transactionId ? { transactionId } : {}) },
  );
};
